/**
 * ConfirmChangeRoleDialog Component
 *
 * Confirmation modal for toggling a member's platform role (member ⇄ admin).
 * A role change is significant — admins can invite and remove members — but
 * reversible, so this is a plain confirm (no type-to-confirm gesture like the
 * destructive delete dialog).
 *
 * The dialog is "dumb" about which org it acts on: the store's `updateUserRole`
 * action resolves the effective org id. This component only needs the target
 * {@link User} (whose current role determines the toggle direction) and the
 * async confirm handler.
 *
 * @module components/ConfirmChangeRoleDialog
 */

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from '@mui/material';
import type { User, UserRole } from '../types';
import { isUpdateRoleError, UPDATE_ROLE_ERROR_MESSAGES } from '../stores/useUsersStore';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the ConfirmChangeRoleDialog component.
 */
export interface ConfirmChangeRoleDialogProps {
  /**
   * The user whose role to change. `null` keeps the dialog closed; a non-null
   * value opens it. The target role is the opposite of the user's current role.
   */
  user: User | null;

  /** Callback when the dialog should close (cancel or after a successful change). */
  onClose: () => void;

  /**
   * Async handler that performs the role change. Resolves on success; rejects
   * with an {@link UpdateRoleError} (or any Error) on failure. The dialog shows
   * a pending label while it's in flight and an inline alert if it rejects.
   */
  onConfirm: (userId: string, role: UserRole) => Promise<void>;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Best-effort human label for the user: display name, else the given/family
 * name, else the email. Mirrors the data grid's Name column.
 */
function userLabel(user: User): string {
  if (user.displayName) {
    return user.displayName;
  }
  const fullName = [user.givenName, user.familyName].filter(Boolean).join(' ');
  if (fullName) {
    return fullName;
  }
  return user.email;
}

// =============================================================================
// ConfirmChangeRoleDialog Component
// =============================================================================

/**
 * ConfirmChangeRoleDialog - Confirmation modal for a member ⇄ admin toggle.
 *
 * Features:
 * - Names the target user and states the consequence of the new role
 * - Loading state while the change is in flight (buttons disabled)
 * - Inline error alert on failure; the dialog stays open so the admin can retry
 * - Resets its transient state each time it (re)opens
 */
export function ConfirmChangeRoleDialog({
  user,
  onClose,
  onConfirm,
}: ConfirmChangeRoleDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const open = user !== null;

  // Reset transient state on each open transition (adjust-state-during-render
  // pattern, matching the other dialogs).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setError(null);
      setIsSubmitting(false);
    }
  }

  // The toggle direction: promoting a member, or demoting an admin.
  const targetRole: UserRole = user?.role === 'admin' ? 'member' : 'admin';
  const promoting = targetRole === 'admin';

  const handleCancel = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    onClose();
  }, [isSubmitting, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!user || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(user.id, targetRole);
      // Success — parent shows a snackbar; close the dialog.
      onClose();
    } catch (err) {
      const message = isUpdateRoleError(err)
        ? err.message
        : UPDATE_ROLE_ERROR_MESSAGES.GENERIC;
      setError(message);
      setIsSubmitting(false);
    }
  }, [user, isSubmitting, targetRole, onConfirm, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      data-testid="change-role-dialog"
      aria-labelledby="change-role-dialog-title"
    >
      <DialogTitle
        id="change-role-dialog-title"
        data-testid="change-role-dialog-title"
      >
        {promoting ? 'Make admin' : 'Change to member'}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="change-role-error">
            {error}
          </Alert>
        )}

        {user && (
          <DialogContentText data-testid="change-role-dialog-text">
            {promoting ? (
              <>
                Make <strong>{userLabel(user)}</strong> ({user.email}) an{' '}
                <strong>admin</strong>? Admins can invite and remove members and
                manage other members&apos; roles.
              </>
            ) : (
              <>
                Change <strong>{userLabel(user)}</strong> ({user.email}) to a
                regular <strong>member</strong>? They will lose the ability to
                manage members of this organization.
              </>
            )}
          </DialogContentText>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          data-testid="change-role-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isSubmitting}
          data-testid="change-role-confirm-button"
        >
          {isSubmitting
            ? 'Saving…'
            : promoting
              ? 'Make admin'
              : 'Change to member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
